import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  dark:    [30,  41,  59],
  green:   [16,  185, 129],
  amber:   [217, 119, 6],
  red:     [239, 68,  68],
  blue:    [59,  130, 246],
  purple:  [139, 92,  246],
  gray:    [100, 116, 139],
  light:   [248, 249, 250],
  border:  [226, 232, 240],
  white:   [255, 255, 255],
  accent:  [15,  118, 110],
};

const scoreCol  = s => s >= 8 ? C.green : s >= 6 ? C.amber : C.red;
const scoreGrade = s => s >= 8 ? 'EXCELLENT' : s >= 6 ? 'GOOD' : s >= 4 ? 'FAIR' : 'POOR';
const fmtKey    = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const PAGE_W    = 210; // A4 mm
const MARGIN    = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;

// ── Horizontal rule ───────────────────────────────────────────────────────────
function rule(doc, y, color = C.border, lw = 0.3) {
  doc.setDrawColor(...color);
  doc.setLineWidth(lw);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
}

// ── Section heading with left accent bar ─────────────────────────────────────
function sectionHead(doc, title, y, color = C.dark) {
  doc.setFillColor(...color);
  doc.rect(MARGIN, y - 1, 3, 8, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...color);
  doc.text(title.toUpperCase(), MARGIN + 6, y + 5.5);
  rule(doc, y + 9, C.border);
  return y + 14;
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function statPill(doc, label, value, x, y, color = C.dark, w = 38, h = 20) {
  // shadow effect
  doc.setFillColor(220, 220, 220);
  doc.roundedRect(x + 0.5, y + 0.5, w, h, 3, 3, 'F');
  doc.setFillColor(...C.white);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, 3, 3, 'FD');
  // top color strip
  doc.setFillColor(...color);
  doc.roundedRect(x, y, w, 4, 2, 2, 'F');
  doc.rect(x, y + 2, w, 2, 'F');
  // value
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...color);
  doc.text(String(value), x + w / 2, y + 12, { align: 'center' });
  // label
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  doc.text(label.toUpperCase(), x + w / 2, y + 17, { align: 'center' });
}

// ── Score gauge bar ───────────────────────────────────────────────────────────
function scoreBar(doc, score, x, y, w = 80) {
  const h = 5;
  // track
  doc.setFillColor(...C.border);
  doc.roundedRect(x, y, w, h, 2, 2, 'F');
  // fill
  const fillW = Math.max(2, (score / 10) * w);
  doc.setFillColor(...scoreCol(score));
  doc.roundedRect(x, y, fillW, h, 2, 2, 'F');
  // tick marks at 6 and 8
  [6, 8].forEach(t => {
    const tx = x + (t / 10) * w;
    doc.setDrawColor(...C.gray);
    doc.setLineWidth(0.2);
    doc.line(tx, y - 1, tx, y + h + 1);
  });
}

// ── Page header ───────────────────────────────────────────────────────────────
function pageHeader(doc, exerciseName, pageNum) {
  doc.setFillColor(...C.dark);
  doc.rect(0, 0, PAGE_W, 16, 'F');
  // left accent stripe
  doc.setFillColor(...C.green);
  doc.rect(0, 0, 4, 16, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text('RETICULO  ·  Movement Analysis', 8, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(180, 200, 220);
  doc.text(`${exerciseName}  ·  Page ${pageNum}`, PAGE_W - MARGIN, 10, { align: 'right' });
}

// ── Page footer ───────────────────────────────────────────────────────────────
function pageFooter(doc, date, pageNum) {
  const h = doc.internal.pageSize.getHeight();
  doc.setFillColor(...C.dark);
  doc.rect(0, h - 10, PAGE_W, 10, 'F');
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 200, 220);
  doc.text(`Generated ${date}`, MARGIN, h - 3.5);
  doc.text('Confidential – For personal use only', PAGE_W / 2, h - 3.5, { align: 'center' });
  if (pageNum) doc.text(`Page ${pageNum}`, PAGE_W - MARGIN, h - 3.5, { align: 'right' });
}

// ── Guard: new page if needed ─────────────────────────────────────────────────
function guard(doc, y, need = 30, exerciseName = '', pageNum = { n: 1 }, date = '') {
  const h = doc.internal.pageSize.getHeight();
  if (y + need > h - 16) {
    pageFooter(doc, date, pageNum.n);
    doc.addPage();
    pageNum.n += 1;
    pageHeader(doc, exerciseName, pageNum.n);
    return 24;
  }
  return y;
}

// ── Render one exercise result ────────────────────────────────────────────────
function renderResult(doc, video, result, isFirst, pageNum, date) {
  const score = typeof result.average_rating === 'number'
    ? result.average_rating
    : parseFloat(result.average_rating) || 0;
  const reps      = result.total_reps ?? 0;
  const ratings   = result.ratings || result.ratings_per_rep || [];
  const duration  = result.duration_seconds;
  const exerciseName = video.exerciseName || result.exercise || 'Exercise';

  if (!isFirst) {
    pageFooter(doc, date, pageNum.n);
    doc.addPage();
    pageNum.n += 1;
  }
  pageHeader(doc, exerciseName, pageNum.n);

  let y = 22;

  // ── Title block ──────────────────────────────────────────────────────────────
  doc.setFillColor(...C.dark);
  doc.roundedRect(MARGIN, y, CONTENT_W, 26, 3, 3, 'F');
  doc.setFillColor(...C.green);
  doc.roundedRect(MARGIN, y, 5, 26, 2, 2, 'F');
  doc.rect(MARGIN + 3, y, 2, 26, 'F');

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text(exerciseName, MARGIN + 10, y + 11);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 200, 220);
  doc.text(`File: ${video.videoName || exerciseName}`, MARGIN + 10, y + 20);
  doc.text(`Date: ${date}`, PAGE_W - MARGIN - 2, y + 20, { align: 'right' });

  y += 32;

  // ── Score section ────────────────────────────────────────────────────────────
  const sc = scoreCol(score);
  const grade = scoreGrade(score);

  // Score circle
  const cx = MARGIN + 18, cy = y + 16;
  doc.setFillColor(...C.border);
  doc.circle(cx, cy, 15.5, 'F');
  doc.setFillColor(...sc);
  doc.circle(cx, cy, 14, 'F');
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text(score.toFixed(1), cx, cy + 2, { align: 'center' });
  doc.setFontSize(6.5);
  doc.text('/10', cx, cy + 8, { align: 'center' });

  // Grade badge
  doc.setFillColor(...sc);
  doc.roundedRect(cx - 12, cy + 12, 24, 7, 2, 2, 'F');
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text(grade, cx, cy + 17, { align: 'center' });

  // Score bar
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  doc.text('Performance', MARGIN + 36, y + 8);
  scoreBar(doc, score, MARGIN + 36, y + 10, 90);
  doc.setFontSize(6.5);
  doc.setTextColor(...C.gray);
  doc.text('Poor', MARGIN + 36, y + 19);
  doc.text('Fair', MARGIN + 36 + 90 * 0.4, y + 19, { align: 'center' });
  doc.text('Good', MARGIN + 36 + 90 * 0.6, y + 19, { align: 'center' });
  doc.text('Excellent', MARGIN + 36 + 90, y + 19, { align: 'right' });

  // Stat pills — fit 4 pills in remaining width
  const pillsData = [
    { label: 'Total Reps', value: reps,                                                    color: C.dark   },
    { label: 'Best Rep',   value: ratings.length ? Math.max(...ratings).toFixed(1) : '—', color: C.green  },
    { label: 'Worst Rep',  value: ratings.length ? Math.min(...ratings).toFixed(1) : '—', color: C.red    },
    { label: 'Duration',   value: duration != null ? `${duration.toFixed(1)}s` : '—',     color: C.blue   },
  ];
  const pillStart = MARGIN + 36;
  const pillW = (PAGE_W - MARGIN - pillStart - 2) / pillsData.length - 2;
  pillsData.forEach((p, i) => statPill(doc, p.label, p.value, pillStart + i * (pillW + 2), y + 22, p.color, pillW, 20));

  y += 50;

  // ── Angle range ──────────────────────────────────────────────────────────────
  if (result.angle_range) {
    y = guard(doc, y, 44, exerciseName, pageNum, date);
    y = sectionHead(doc, 'Angle Range', y, C.blue);
    const ar  = result.angle_range;
    const thr = result.thresholds;
    const anglePills = [
      { label: 'Min Angle',  value: `${ar.min.toFixed(1)}°`, color: C.red   },
      { label: 'Avg Angle',  value: `${ar.avg.toFixed(1)}°`, color: C.dark  },
      { label: 'Max Angle',  value: `${ar.max.toFixed(1)}°`, color: C.green },
    ];
    if (thr) {
      anglePills.push({ label: 'Down Thr.',  value: `${thr.down.toFixed(1)}°`,            color: C.amber  });
      anglePills.push({ label: 'Up Thr.',    value: `${thr.up.toFixed(1)}°`,              color: C.green  });
      anglePills.push({ label: 'Work Range', value: `${(thr.up - thr.down).toFixed(1)}°`, color: C.purple });
    }
    const apW = (CONTENT_W - (anglePills.length - 1) * 2) / anglePills.length;
    anglePills.forEach((p, i) => statPill(doc, p.label, p.value, MARGIN + i * (apW + 2), y, p.color, apW, 20));
    y += 28;
  }

  // ── Per-rep ratings ───────────────────────────────────────────────────────────
  if (ratings.length > 0) {
    y = guard(doc, y, 44, exerciseName, pageNum, date);
    y = sectionHead(doc, 'Per-Rep Ratings', y, C.accent);

    const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    const repRows = ratings.map((r, i) => {
      const grade = r >= 8 ? 'Good' : r >= 6 ? 'OK' : 'Poor';
      return [`Rep ${i + 1}`, r.toFixed(2), grade];
    });

    autoTable(doc, {
      startY: y,
      head: [['Rep', 'Rating /10', 'Grade']],
      body: repRows,
      foot: [['Average', avg.toFixed(2), avg >= 8 ? 'Good' : avg >= 6 ? 'OK' : 'Poor']],
      theme: 'grid',
      headStyles: { fillColor: C.dark,  textColor: C.white, fontSize: 8, fontStyle: 'bold', halign: 'center' },
      footStyles: { fillColor: [240, 245, 255], textColor: C.dark, fontSize: 8, fontStyle: 'bold', halign: 'center' },
      bodyStyles: { fontSize: 8, halign: 'center' },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 40 }, 2: { cellWidth: 40 } },
      margin: { left: MARGIN, right: MARGIN },
      didParseCell(data) {
        if (data.section === 'body' && data.column.index === 2) {
          const g = data.cell.raw;
          data.cell.styles.textColor = g === 'Good' ? C.green : g === 'OK' ? C.amber : C.red;
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.section === 'foot' && data.column.index === 2) {
          const g = data.cell.raw;
          data.cell.styles.textColor = g === 'Good' ? C.green : g === 'OK' ? C.amber : C.red;
        }
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Additional metrics ────────────────────────────────────────────────────────
  const skip = new Set(['total_reps','average_rating','ratings','ratings_per_rep',
    'angle_range','thresholds','exercise','exerciseName','timestamp',
    'video_info','corrections','feedback','warnings','duration_seconds',
    'best_rating','worst_rating']);

  const extras = Object.entries(result).filter(([k, v]) =>
    !skip.has(k) && v !== null && v !== undefined
  );

  const baseMetrics = [];
  if (result.best_rating   != null) baseMetrics.push(['Best Rating',  `${result.best_rating.toFixed(2)}/10`]);
  if (result.worst_rating  != null) baseMetrics.push(['Worst Rating', `${result.worst_rating.toFixed(2)}/10`]);
  if (result.duration_seconds != null) baseMetrics.push(['Duration',  `${result.duration_seconds.toFixed(1)} s`]);
  if (result.video_info?.fps)    baseMetrics.push(['FPS',    result.video_info.fps.toFixed(1)]);
  if (result.video_info?.frames) baseMetrics.push(['Frames', String(result.video_info.frames)]);

  const extraMetrics = [];
  extras.forEach(([k, v]) => {
    if (typeof v === 'object' && !Array.isArray(v)) {
      Object.entries(v).forEach(([sk, sv]) =>
        extraMetrics.push([`${fmtKey(k)} – ${fmtKey(sk)}`, typeof sv === 'number' ? sv.toFixed(2) : String(sv)])
      );
    } else if (typeof v === 'number') {
      extraMetrics.push([fmtKey(k), v.toFixed(2)]);
    } else if (typeof v === 'string') {
      extraMetrics.push([fmtKey(k), v]);
    }
  });

  const allMetrics = [...baseMetrics, ...extraMetrics];
  if (allMetrics.length > 0) {
    y = guard(doc, y, 44, exerciseName, pageNum, date);
    y = sectionHead(doc, 'Additional Metrics', y, C.purple);
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: allMetrics,
      theme: 'striped',
      headStyles: { fillColor: C.dark, textColor: C.white, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 245, 255] },
      columnStyles: { 0: { cellWidth: 110 }, 1: { cellWidth: 68 } },
      margin: { left: MARGIN, right: MARGIN },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Insights ──────────────────────────────────────────────────────────────────
  const feedback    = result.feedback    || [];
  const corrections = result.corrections || [];
  const warnings    = result.warnings    || [];

  if (feedback.length || corrections.length || warnings.length) {
    y = guard(doc, y, 34, exerciseName, pageNum, date);
    y = sectionHead(doc, 'Insights', y, C.amber);

    const insightRows = [
      ...feedback.map(f    => ['✔ Strength',   f, C.green]),
      ...corrections.map(c => ['⚠ Correction', c, C.amber]),
      ...warnings.map(w    => ['✖ Warning',     w, C.red  ]),
    ];

    autoTable(doc, {
      startY: y,
      head: [['Type', 'Detail']],
      body: insightRows.map(r => [r[0], r[1]]),
      theme: 'plain',
      headStyles: { fillColor: C.dark, textColor: C.white, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, cellPadding: 3 },
      alternateRowStyles: { fillColor: [252, 250, 245] },
      columnStyles: { 0: { cellWidth: 28, fontStyle: 'bold' }, 1: { cellWidth: CONTENT_W - 28 } },
      margin: { left: MARGIN, right: MARGIN },
      didParseCell(data) {
        if (data.section === 'body' && data.column.index === 0) {
          const color = insightRows[data.row.index]?.[2];
          if (color) data.cell.styles.textColor = color;
        }
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  return y;
}

// ── Single-video export ───────────────────────────────────────────────────────
export const generatePDFReport = (video, result) => {
  const doc     = new jsPDF({ unit: 'mm', format: 'a4' });
  const date    = new Date().toLocaleString();
  const pageNum = { n: 1 };

  renderResult(doc, video, result, true, pageNum, date);
  pageFooter(doc, date, pageNum.n);

  const name = (video.videoName || video.exerciseName || 'report')
    .replace(/[^a-z0-9]/gi, '_').toLowerCase();
  doc.save(`analysis-${name}.pdf`);
};

// ── Multi-video export ────────────────────────────────────────────────────────
export const generateOverviewPDF = (videos) => {
  const analyzed = videos.filter(v => v.result);
  if (analyzed.length === 0) return;

  const doc     = new jsPDF({ unit: 'mm', format: 'a4' });
  const date    = new Date().toLocaleString();
  const pageNum = { n: 1 };

  // ── Cover page ───────────────────────────────────────────────────────────────
  // Hero band
  doc.setFillColor(...C.dark);
  doc.rect(0, 0, PAGE_W, 70, 'F');
  doc.setFillColor(...C.green);
  doc.rect(0, 0, PAGE_W, 4, 'F');

  // Decorative circles
  doc.setFillColor(255, 255, 255, 0.03);
  doc.setDrawColor(...C.green);
  doc.setLineWidth(0.5);
  doc.circle(PAGE_W - 30, 20, 28, 'D');
  doc.circle(PAGE_W - 30, 20, 18, 'D');

  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text('Movement Analysis', PAGE_W / 2, 28, { align: 'center' });
  doc.setFontSize(24);
  doc.setTextColor(...C.green);
  doc.text('Report', PAGE_W / 2, 40, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 200, 220);
  doc.text('RETICULO  ·  Comprehensive Performance Summary', PAGE_W / 2, 52, { align: 'center' });
  doc.text(date, PAGE_W / 2, 61, { align: 'center' });

  // Summary stat pills
  const avgScore  = analyzed.reduce((s, v) => s + (parseFloat(v.result.average_rating) || 0), 0) / analyzed.length;
  const totalReps = analyzed.reduce((s, v) => s + (v.result.total_reps || 0), 0);
  const bestScore = Math.max(...analyzed.map(v => parseFloat(v.result.average_rating) || 0));

  let y = 78;
  const coverPills = [
    { label: 'Videos Analyzed', value: analyzed.length,    color: C.blue   },
    { label: 'Total Reps',      value: totalReps,           color: C.accent },
    { label: 'Avg Score',       value: avgScore.toFixed(1), color: scoreCol(avgScore) },
    { label: 'Best Score',      value: bestScore.toFixed(1),color: C.green  },
  ];
  const cpW = (CONTENT_W - (coverPills.length - 1) * 3) / coverPills.length;
  coverPills.forEach((p, i) => statPill(doc, p.label, p.value, MARGIN + i * (cpW + 3), y, p.color, cpW, 24));
  y += 32;

  // Overall score bar
  y += 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text('Session Average Score', MARGIN, y);
  scoreBar(doc, avgScore, MARGIN, y + 3, CONTENT_W);
  y += 14;

  // Summary table
  y = sectionHead(doc, 'Session Summary', y + 2);
  const summaryRows = analyzed.map((v, i) => {
    const r = v.result;
    const s = parseFloat(r.average_rating) || 0;
    return [
      `#${i + 1}`,
      v.exerciseName || r.exercise || '—',
      v.videoName || '—',
      `${s.toFixed(1)}/10`,
      String(r.total_reps ?? 0),
      r.duration_seconds != null ? `${r.duration_seconds.toFixed(1)}s` : '—',
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['#', 'Exercise', 'File', 'Score', 'Reps', 'Duration']],
    body: summaryRows,
    theme: 'grid',
    headStyles: { fillColor: C.dark, textColor: C.white, fontSize: 8, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 8, halign: 'center' },
    alternateRowStyles: { fillColor: [245, 248, 255] },
    columnStyles: { 1: { halign: 'left' }, 2: { halign: 'left' } },
    margin: { left: MARGIN, right: MARGIN },
    didParseCell(data) {
      if (data.section === 'body' && data.column.index === 3) {
        const s = parseFloat(data.cell.raw);
        data.cell.styles.textColor = scoreCol(s);
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  pageFooter(doc, date, pageNum.n);

  // ── Per-video pages ───────────────────────────────────────────────────────────
  analyzed.forEach((video) => {
    renderResult(doc, video, video.result, false, pageNum, date);
    pageFooter(doc, date, pageNum.n);
  });

  doc.save(`reticulo-overview-${Date.now()}.pdf`);
};
